import React, { useState, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { transactionAPI } from '../../services/api';
import DetailsPanel from '../../components/ui/DetailsPanel';

const TransactionHistory = () => {
  const { id } = useParams();
  const history = useHistory();
  const [transaction, setTransaction] = useState(null);
  const [historyEvents, setHistoryEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch transaction details and history
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch both transaction details and history
        const [transactionResponse, historyResponse] = await Promise.all([
          transactionAPI.getById(id),
          transactionAPI.getHistory(id)
        ]);
        
        
        setTransaction(transactionResponse.data);
        
        // Process timeline events from backend
        const timelineEvents = historyResponse.data || [];
        
        // Transform backend timeline events to frontend format
        const processedEvents = timelineEvents.map(event => {
          // Determine event type
          let eventType = 'other';
          let description = event.action;
          let verificationType = null; // 'CLIENT' or 'COMPTABILITE' or null
          
          if (event.action === 'TRANSACTION_CREATED') {
            eventType = 'created';
            description = 'Transaction créée';
          } else if (event.action === 'DESCRIPTION_UPDATED') {
            eventType = 'description_changed';
            description = 'Description modifiée';
          // Legacy VERIFICATION_STATUS_UPDATED - commented out after migration
          // } else if (event.action === 'VERIFICATION_STATUS_UPDATED') {
          //   eventType = 'verification_status_changed';
          //   description = 'Statut de vérification modifié';
          } else if (event.action === 'VERIFICATION_STATUS_CLIENT_UPDATED') {
            eventType = 'verification_status_changed';
            description = 'Statut de vérification client modifié';
            verificationType = 'CLIENT';
          } else if (event.action === 'VERIFICATION_STATUS_COMPTABILITE_UPDATED') {
            eventType = 'verification_status_changed';
            description = 'Statut de vérification comptabilité modifié';
            verificationType = 'COMPTABILITE';
          }
          
          return {
            id: event.id,
            type: eventType,
            agent: event.createdBy ? 
              `${event.createdBy.firstName} ${event.createdBy.lastName}` : 
              'Utilisateur inconnu',
            timestamp: event.createdAt,
            description: description,
            verificationType: verificationType,
            oldValue: event.oldValue,
            newValue: event.newValue
          };
        });
        
        setHistoryEvents(processedEvents);
      } catch (error) {
        console.error('Error fetching transaction history:', error);
        setHistoryEvents([]);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchData();
    }
  }, [id]);

  const handleClose = () => {
    history.goBack();
  };

  const formatHistoryDate = (dateString) => {
    if (!dateString) return 'Date inconnue';
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
  };

  if (loading) {
    return (
      <DetailsPanel onClose={handleClose}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <Typography>Chargement...</Typography>
        </Box>
      </DetailsPanel>
    );
  }

  if (!transaction) {
    return (
      <DetailsPanel onClose={handleClose}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <Typography>Transaction non trouvée</Typography>
        </Box>
      </DetailsPanel>
    );
  }

  return (
    <DetailsPanel onClose={handleClose}>
      {/* Title */}
      <Typography
        sx={{
          fontFamily: 'Montserrat',
          fontSize: '22px',
          fontWeight: 600,
          color: '#000000',
          textTransform: 'none',
          marginBottom: '20px'
        }}
      >
        Historique de la transaction
      </Typography>

      {/* Timeline */}
      <Box sx={{ position: 'relative' }}>
        {/* Timeline Line */}
        <Box
          sx={{
            position: 'absolute',
            left: '5px',
            top: '0px',
            width: '2px',
            height: '100%',
            backgroundColor: '#E3E3E3',
            zIndex: 1
          }}
        />

        {/* Timeline Events */}
        {historyEvents.map((event, index) => (
          <Box key={event.id} sx={{ position: 'relative', marginBottom: '20px', paddingLeft: '30px' }}>
            {/* Timeline Dot */}
            <Box
              sx={{
                position: 'absolute',
                left: '0px',
                top: '0px',
                width: '12px',
                height: '12px',
                backgroundColor: '#027BC6',
                borderRadius: '50%',
                zIndex: 2,
                boxShadow: '0px 20px 80px #00000011'
              }}
            />

            {/* Event Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Typography
                  sx={{
                    fontFamily: 'Montserrat',
                    fontSize: '16px',
                    fontWeight: 600,
                    color: '#38485C',
                    textTransform: 'none'
                  }}
                >
                  {event.agent}
                </Typography>
                {/* Type pill */}
                <Box
                  sx={{
                    padding: '2px 8px',
                    borderRadius: '9999px',
                    fontFamily: 'Montserrat',
                    fontSize: '11px',
                    fontWeight: 700,
                    textTransform: 'none',
                    lineHeight: 1.6,
                    color: (event.type === 'created') ? '#0B5C2B' : (event.type === 'description_changed') ? '#5C3C0B' : (event.type === 'verification_status_changed') ? '#0B3C5C' : '#38485C',
                    backgroundColor: (event.type === 'created') ? '#E6F4EA' : (event.type === 'description_changed') ? '#FFF2E0' : (event.type === 'verification_status_changed') ? '#E6F0FA' : '#F1F5F9',
                    border: '1px solid',
                    borderColor: (event.type === 'created') ? '#B7E0C3' : (event.type === 'description_changed') ? '#F6D7B0' : (event.type === 'verification_status_changed') ? '#C8D8F0' : '#E2E8F0'
                  }}
                >
                  {event.type === 'created' ? 'Création' : event.type === 'description_changed' ? 'Modification' : event.type === 'verification_status_changed' ? 'Statut de vérification' : 'Événement'}
                </Box>
              </Box>
              <Typography
                sx={{
                  fontFamily: 'Montserrat',
                  fontSize: '12px',
                  fontWeight: 500,
                  color: '#445E76',
                  textTransform: 'none',
                  opacity: 0.88
                }}
              >
                {formatHistoryDate(event.timestamp)}
              </Typography>
            </Box>

            {/* Event Content */}
            <Box
              sx={{
                backgroundColor: '#FFFFFF',
                border: '1px solid #E3E3E374',
                borderRadius: '8px',
                padding: '12px',
                boxShadow: '0px 20px 80px #00000011'
              }}
            >
              {event.type === 'created' && (
                <Typography
                  sx={{
                    fontFamily: 'Montserrat',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#000000',
                    textTransform: 'none',
                    opacity: 1
                  }}
                >
                  Transaction créée
                </Typography>
              )}

              {event.type === 'description_changed' && (
                <Box>
                  <Typography
                    sx={{
                      fontFamily: 'Montserrat',
                      fontSize: '14px',
                      fontWeight: 600,
                      color: '#000000',
                      textTransform: 'none',
                      opacity: 1,
                      marginBottom: '8px'
                    }}
                  >
                    Description modifiée de :
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'Montserrat',
                      fontSize: '14px',
                      fontWeight: 600,
                      color: '#707070',
                      textTransform: 'none',
                      opacity: 0.88,
                      lineHeight: '22px',
                      marginBottom: '8px'
                    }}
                  >
                    {event.oldValue || 'N/A'}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'Montserrat',
                      fontSize: '14px',
                      fontWeight: 600,
                      color: '#000000',
                      textTransform: 'none',
                      opacity: 1,
                      marginBottom: '8px'
                    }}
                  >
                    À :
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'Montserrat',
                      fontSize: '14px',
                      fontWeight: 600,
                      color: '#707070',
                      textTransform: 'none',
                      opacity: 0.88,
                      lineHeight: '22px'
                    }}
                  >
                    {event.newValue || 'N/A'}
                  </Typography>
                </Box>
              )}

          {event.type === 'verification_status_changed' && (
            <Box>
              <Typography
                sx={{
                  fontFamily: 'Montserrat',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#000000',
                  textTransform: 'none',
                  opacity: 1,
                  marginBottom: '8px'
                }}
              >
                {event.verificationType === 'CLIENT' ? 'Statut de vérification client modifié de :' :
                 event.verificationType === 'COMPTABILITE' ? 'Statut de vérification comptabilité modifié de :' :
                 'Statut de vérification modifié de :'}
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Montserrat',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#707070',
                  textTransform: 'none',
                  opacity: 0.88,
                  lineHeight: '22px',
                  marginBottom: '8px'
                }}
              >
                {event.oldValue || 'N/A'}
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Montserrat',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#000000',
                  textTransform: 'none',
                  opacity: 1,
                  marginBottom: '8px'
                }}
              >
                À :
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Montserrat',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#707070',
                  textTransform: 'none',
                  opacity: 0.88,
                  lineHeight: '22px'
                }}
              >
                {event.newValue || 'N/A'}
              </Typography>
            </Box>
          )}

              {/* Generic event display for other event types */}
              {event.type !== 'created' && event.type !== 'description_changed' && event.type !== 'verification_status_changed' && (
                <Typography
                  sx={{
                    fontFamily: 'Montserrat',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#000000',
                    textTransform: 'none',
                    opacity: 1
                  }}
                >
                  {event.description || event.message || event.type}
                </Typography>
              )}
            </Box>
          </Box>
        ))}
      </Box>
    </DetailsPanel>
  );
};

export default TransactionHistory;
